import React from "react";
import { SidebarProps, StepStatus } from "../../common/types";
import { CheckIcon, CloseIcon, GridIcon } from "../../common/Icons";

const Sidebar: React.FC<SidebarProps> = ({
  steps,
  currentViewedStepId,
  onStepSelect,
  onGoToAllItems,
  onCloseMenuClick,
  className,
  ariaId,
  isMobileMenuOpen,
  stepCompletionStatus,
}) => {
  const baseItemClasses =
    "flex items-center p-3 my-1.5 rounded-md transition-all duration-200 ease-in-out cursor-pointer";

  let allItemsClasses = `${baseItemClasses} `;
  let allItemsIconColor = "text-gray-500";
  let allItemsTextClasses = "ml-3 font-medium whitespace-nowrap ";

  if (currentViewedStepId === 0) {
    allItemsClasses += "bg-blue-50 border-l-4 border-blue-500 shadow-sm";
    allItemsTextClasses += "text-blue-700";
    allItemsIconColor = "text-blue-600";
  } else {
    allItemsClasses += "border-l-4 border-transparent hover:bg-gray-100";
    allItemsTextClasses += "text-gray-600";
  }

  return (
    <aside
      id={ariaId}
      className={`
        fixed top-0 left-0 h-full z-40 
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
        md:relative md:translate-x-0 
        w-3/4 max-w-xs md:w-auto md:flex-shrink-0 md:h-full
        bg-white shadow-xl md:shadow-lg md:rounded-lg 
        ${className || ""}
      `}
    >
      <div className="p-4 h-full md:overflow-y-auto">
        <div className="flex justify-between items-center mb-4 md:hidden">
          <span className="text-xl font-semibold text-blue-700">Menu</span>
          <button
            onClick={onCloseMenuClick}
            className="text-gray-600 hover:text-gray-800 p-1 -m-1"
            aria-label="Close menu"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
        <nav aria-label="Progress steps">
          <ul>
            <li
              className={allItemsClasses}
              onClick={onGoToAllItems}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onGoToAllItems();
                }
              }}
              tabIndex={0}
              role="menuitem"
              aria-current={currentViewedStepId === 0 ? "page" : undefined}
            >
              <GridIcon
                className={`w-6 h-6 ${allItemsIconColor} flex-shrink-0`}
              />
              <span className={`${allItemsTextClasses}`}>All Items</span>
            </li>

            {steps.length > 0 && <hr className="my-2 border-gray-200" />}

            {steps.map((step) => {
              const IconComponent = step.icon;
              // Check both the original status and our fetched completion status
              const isCompleted =
                step.status === StepStatus.COMPLETED ||
                stepCompletionStatus[step.id] === true;

              let itemClasses = `${baseItemClasses} `;
              let textClasses = "ml-3 font-medium ";
              let iconColor = "text-gray-500";

              if (isCompleted) {
                itemClasses +=
                  "bg-green-50 border-l-4 border-green-500 hover:bg-green-100";
                textClasses += "text-green-700";
                iconColor = "text-green-600";
              } else if (step.id === currentViewedStepId) {
                itemClasses +=
                  "bg-blue-50 border-l-4 border-blue-500 shadow-sm";
                textClasses += "text-blue-700";
                iconColor = "text-blue-600";
              } else {
                itemClasses +=
                  "border-l-4 border-transparent hover:bg-gray-100";
                textClasses += "text-gray-600";
              }

              return (
                <li
                  key={step.id}
                  className={itemClasses}
                  onClick={() => onStepSelect(step.id)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onStepSelect(step.id);
                    }
                  }}
                  tabIndex={0}
                  role="menuitem"
                  aria-current={
                    step.id === currentViewedStepId ? "step" : undefined
                  }
                >
                  {isCompleted && (
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                  )}
                  <IconComponent
                    className={`w-6 h-6 ${iconColor} ${
                      isCompleted ? "ml-1.5" : ""
                    } flex-shrink-0`}
                  />
                  <span className={`${textClasses}`}>{step.title}</span>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
